<script>
import Repositories from '@/repositories/no-authorization';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/fr';
import dateMixin from '@/mixins/dateMixin';
import 'vue-loading-overlay/dist/vue-loading.css';
import Loading from 'vue-loading-overlay';

export default {
  props: {
    orderIdPublic: String,
  },
  components: {
    DatePicker,
    Loading,
  },
  mixins: [dateMixin],
  data() {
    return {
      confirmationStatus: 0,
      orderRepository: null,
      silentUpdate: false,
      order: null,
      isLoading: false,
      metadataEdition: {
        isEditingExternalNumber: false,
        isEditingReceptionDate: false,
        isLoadingExternalNumber: false,
        isLoadingReceptionDate: false,
        isErrorExternalNumber: false,
        isErrorReceptionDate: false,
      },
    };
  },
  created() {
    this.orderRepository = Repositories.get(
      'orders',
      this.$route.params.orgcode
    );

    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;

      this.orderRepository
        .get(this.orderIdPublic)
        .then(({ data }) => {
          this.isLoading = false;
          this.order = data;

          if (this.order.supplierStatus == 'EMAIL_SENT') {
            this.silentUpdate = true;
            this.order.supplierStatus = 'OPENED';
            this.orderRepository
              .update(this.order, this.order.idPublic)
              .then(() => {
                this.silentUpdate = false;
              })
              .catch(() => {
                this.silentUpdate = false;
              });
          }
        })
        .catch(() => {
          this.isLoading = false;
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        });
    },
    confirmStatus() {
      if (
        !this.order.isOrdered &&
        this.order.supplierStatus != 'CONFIRMED' &&
        this.confirmationStatus != 1
      ) {
        this.confirmationStatus = 1;

        let apiWrapper = { ...this.order, supplierStatus: 'CONFIRMED' };
        this.orderRepository
          .update(apiWrapper, this.order.idPublic)
          .then(({ data }) => {
            this.metadataEdition.isEditingExternalNumber = false;
            this.metadataEdition.isEditingReceptionDate = false;

            apiWrapper.supplierStatus = data.supplierStatus;
            apiWrapper.isOrdered = data.isOrdered;

            this.order = apiWrapper;
          })
          .catch(() => {
            this.confirmationStatus = 2;
          });
      }
    },
    startEditing(field) {
      this.order[`is${field}UpdatedExternal`] = true;
      this.metadataEdition[`isEditing${field}`] = true;
      this.$nextTick(() => {
        const editInput = this.$refs[`edit${field}Input`];
        editInput.focus();
      });
    },
    async save(field) {
      if (
        !this.metadataEdition.isLoadingExternalNumber &&
        !this.metadataEdition.isLoadingReceptionDate
      ) {
        this.metadataEdition[`isLoading${field}`] = true;
        this.metadataEdition[`isError${field}`] = false;

        await this.$nextTick();

        try {
          await this.orderRepository.update(this.order, this.order.idPublic);

          this.metadataEdition[`isEditing${field}`] = false;
        } catch (error) {
          this.metadataEdition[`error${field}`] = true;
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        }

        this.metadataEdition[`isLoading${field}`] = false;
      }
    },
    async cancel(field) {
      await this.$nextTick();

      if (this.metadataEdition[`isLoading${field}`] == false) {
        this.metadataEdition[`isEditing${field}`] = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>
    <b-container v-if="order != null" fluid>
      <!-- HEADER NUMBER + CONFIRM BUTTON -->
      <b-row>
        <b-col cols="8">
          <h1>PO - {{ order.number }}</h1>
        </b-col>
        <b-col cols="4" class="text-right pr-0">
          <b-button
            id="popover-server-error"
            :disabled="silentUpdate"
            :variant="
              order.isOrdered
                ? 'success'
                : confirmationStatus == 1 || order.supplierStatus == 'CONFIRMED'
                ? 'warning'
                : confirmationStatus == 0
                ? 'primary'
                : 'danger'
            "
            style="transition: width 1.25s"
            @click="confirmStatus"
          >
            {{
              $t(
                order.isOrdered
                  ? 'orders.orderConfirmed'
                  : order.supplierStatus == 'CONFIRMED'
                  ? 'orders.awaitingApproval'
                  : 'orders.confirmOrder'
              )
            }}
            <Transition name="bounce">
              <i
                v-if="
                  order.supplierStatus == 'CONFIRMED' ||
                  confirmationStatus > 0 ||
                  order.isOrdered
                "
                :class="
                  order.isOrdered
                    ? 'fas fa-check ml-1'
                    : order.supplierStatus == 'CONFIRMED'
                    ? 'fas fa-hourglass-half ml-1'
                    : confirmationStatus == 1
                    ? 'fas fa-spinner fa-spin ml-1'
                    : 'fas fa-exclamation ml-1'
                "
                style="
                  font-size: 1.15em;
                  width: 15px;
                  --fa-animation-duration: 15s;
                "
              ></i>
            </Transition>
          </b-button>
          <b-popover
            v-if="confirmationStatus == 2"
            show
            :target="'popover-server-error'"
            triggers="hover"
            placement="left"
            variant="danger"
          >
            <div style="font-size: 1.2em">
              {{ $t('errors.operationFailedTryAgain') }}
            </div>
          </b-popover>
        </b-col>
      </b-row>

      <!-- EXTERNAL NUMBER WITH EDITION -->
      <b-row class="mt-3">
        <b-col>
          <div style="font-weight: 500; font-size: 1.3em">
            {{ $t('orders.externalNumber') }}
          </div>

          <div
            style="
              max-width: 300px;
              display: flex;
              align-items: center;
              height: 40px;
            "
          >
            <div
              v-if="metadataEdition.isEditingExternalNumber == false"
              style="white-space: nowrap"
            >
              <div
                v-if="order.externalNumber && order.externalNumber.length > 0"
                style="font-size: 1.1em"
              >
                {{ order.externalNumber }}
              </div>
              <div v-else>
                {{ $t('orders.noExternalNumber') }}
              </div>
            </div>
            <b-input
              v-else
              ref="editExternalNumberInput"
              v-model="order.externalNumber"
              style="font-size: 1.1em"
              @keydown.enter.prevent="save('ExternalNumber')"
              @keydown.escape.prevent="cancel('ExternalNumber')"
              type="text"
            ></b-input>
            <div v-if="metadataEdition.isEditingReceptionDate == false">
              <i
                v-if="metadataEdition.isLoadingExternalNumber"
                id="popover-button"
                class="fas fa-spinner fa-spin text-warning ml-3 mr-2"
                @click="save('ExternalNumber')"
                style="font-size: 1.5em"
              ></i>
              <i
                v-else-if="metadataEdition.isErrorExternalNumber"
                @click="save('ExternalNumber')"
                class="fas fa-exclamation text-danger pl-3 pr-2"
                style="cursor: pointer; font-size: 1.5em"
              ></i>
              <i
                v-else-if="metadataEdition.isEditingExternalNumber"
                id="popover-button"
                @click="save('ExternalNumber')"
                class="fas fa-check text-success pl-3 pr-2"
                style="cursor: pointer; font-size: 1.5em"
              ></i>
              <i
                v-else
                id="popover-button"
                class="fas fa-pencil-alt text-primary pl-2 pr-2"
                @click="startEditing('ExternalNumber')"
                style="cursor: pointer; font-size: 1.2em"
              ></i>
            </div>
          </div>
        </b-col>
      </b-row>
      <!-- RECEPTION DATE WITH EDITION -->
      <b-row class="mt-3">
        <b-col>
          <div style="font-weight: 500; font-size: 1.3em">
            {{ $t('orders.receptionDate') }}
          </div>

          <div
            style="
              display: flex;
              height: 40px;
              align-items: center;
              max-width: 300px;
            "
            @keydown.escape.prevent="cancel('ReceptionDate')"
          >
            <div
              v-if="metadataEdition.isEditingReceptionDate == false"
              style="white-space: nowrap"
            >
              <div v-if="order.receptionDate == null">
                {{ $t('orders.noReceptionDate') }}
              </div>
              <div v-else>{{ formatDate(order.receptionDate) }}</div>
            </div>
            <date-picker
              v-else
              ref="editReceptionDateInput"
              v-model="order.receptionDate"
              :first-day-of-week="1"
              format="YYYY-MM-DD"
              value-type="format"
              @input="save('ReceptionDate')"
              style="font-size: 1.1em"
              :lang="$i18n.locale"
            >
            </date-picker>
            <div v-if="metadataEdition.isEditingExternalNumber == false">
              <i
                v-if="metadataEdition.isLoadingReceptionDate"
                id="popover-button"
                class="fas fa-spinner fa-spin text-warning ml-3 mr-2"
                @click="save('ReceptionDate')"
                style="font-size: 1.5em"
              ></i>
              <i
                v-else-if="metadataEdition.isErrorReceptionDate"
                @click="save('ExternalNumber')"
                class="fas fa-exclamation text-danger pl-3 pr-2"
                style="cursor: pointer; font-size: 1.5em"
              ></i>
              <i
                v-else-if="metadataEdition.isEditingReceptionDate"
                id="popover-button"
                @click="save('ReceptionDate')"
                class="fas fa-check text-success pl-3 pr-2"
                style="cursor: pointer; font-size: 1.5em"
              ></i>
              <i
                v-else
                id="popover-button"
                class="fas fa-pencil-alt text-primary pl-2 pr-2"
                @click="startEditing('ReceptionDate')"
                style="cursor: pointer; font-size: 1.2em"
              ></i>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<style scoped>
.bounce-enter-active {
  animation: bounce-in 0.75s;
}
.bounce-leave-active {
  animation: bounce-in 0.75s reverse;
}
@keyframes bounce-in {
  0% {
    transform: scale(0) rotate(-90deg);
    width: 0;
  }
  50% {
    transform: scale(1.25);
    width: 28px;
  }
  100% {
    transform: scale(1) rotate(0deg);
    width: 15px;
  }
}
</style>